import { toast } from "@carbon6/c6-frontend-design";
import { useCallback, useRef, useState, type ReactNode } from "react";
import type { IssuedTrainingShareLinkView } from "@/api/trainingShareLinks";
import { createLogger } from "@/logging";
import { SHARE_LINK_COPIED_MESSAGE, shareLinkIssueErrorMessage } from "./shareLinkMessages";
import { ShareLinkRevealModal } from "./ShareLinkRevealModal";

const logger = createLogger("page/training/shareLinks");

export type ShareLinkIssuer = {
  // 발급 중인 대상의 키. 발급은 상류 파일 목록을 읽어서 몇 초 걸릴 수 있다. 그래서 그 버튼만 잠근다.
  pendingKey: string | null;
  // 발급 → 클립보드 복사 → 토스트. 실패는 토스트로 알리고 null 을 반환한다.
  issue: (
    key: string,
    request: () => Promise<IssuedTrainingShareLinkView>,
  ) => Promise<IssuedTrainingShareLinkView | null>;
  // 클립보드 복사에 실패했을 때 링크를 표시할 모달. 호출부가 트리에 한 번 둔다.
  revealModal: ReactNode;
};

// 공유 버튼의 네 표면(Policy 행·Exported·Raw·공유 링크 탭의 다시 발급)이 같은 발급 흐름을 쓴다.
// 한 번에 하나만 발급한다. 발급마다 행과 공개 링크가 생기므로 연속 클릭으로 기록이 늘지 않게 한다.
export function useShareLinkIssuer(): ShareLinkIssuer {
  const [pendingKey, setPendingKey] = useState<string | null>(null);
  const [revealedUrl, setRevealedUrl] = useState<string | null>(null);
  const inFlight = useRef(false);

  const issue = useCallback<ShareLinkIssuer["issue"]>(async (key, request) => {
    if (inFlight.current) {
      return null;
    }
    inFlight.current = true;
    setPendingKey(key);
    try {
      const issued = await request();
      try {
        await navigator.clipboard.writeText(issued.share_url);
        toast.success(SHARE_LINK_COPIED_MESSAGE);
      } catch (error) {
        // 발급 뒤 await 때문에 사용자 제스처가 끊기면 브라우저가 쓰기를 거절할 수 있다.
        logger.warn("share link clipboard write failed", error);
        setRevealedUrl(issued.share_url);
      }
      return issued;
    } catch (error) {
      logger.error("share link issue failed", error);
      toast.error(shareLinkIssueErrorMessage(error));
      return null;
    } finally {
      inFlight.current = false;
      setPendingKey(null);
    }
  }, []);

  const closeReveal = useCallback(() => setRevealedUrl(null), []);

  return {
    pendingKey,
    issue,
    revealModal: <ShareLinkRevealModal url={revealedUrl} onClose={closeReveal} />,
  };
}
